import { Lightbulb } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useState } from 'react';
import { tipcardsData } from '../data/tipcards';
import { TipCardConfig } from '../types/tipcard';

interface TipCardProps {
  id: string;
  variant?: 'card' | 'list';
  onDismiss?: (tipId: string) => void;
  config?: TipCardConfig; // Optional override config
  isFirstInSection?: boolean;
}

// Icon mapping
const iconMap = {
  lightbulb: Lightbulb,
};

export function TipCard({ id, variant = 'card', onDismiss, config, isFirstInSection = false }: TipCardProps) {
  const tipData = tipcardsData.find(tip => tip.id === id);
  
  if (!tipData) {
    console.error(`TipCard with id "${id}" not found`);
    return null;
  }

  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const IconComponent = iconMap[tipData.icon as keyof typeof iconMap] || Lightbulb;

  // Use config colors if provided (from dynamic section mapping), otherwise use tipData colors
  const backgroundColor = config?.backgroundColorToken || tipData.backgroundColorToken;
  const iconBgColor = config?.iconBgColorToken || tipData.iconBgColorToken;
  const iconColor = config?.iconColorToken || tipData.iconColorToken;
  const displayText = config?.text || tipData.text;
  const displayCtaLabel = config?.ctaLabel || tipData.ctaLabel;

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onDismiss) onDismiss(id);
    }, 400);
  };

  // Horizontal inline banner for list view
  if (variant === 'list') {
    if (!isVisible) return null;

    return (
      <motion.div
        key="tipcard-list"
        className="flex items-start gap-4"
        style={{
          backgroundColor: backgroundColor,
          borderBottom: "1px solid #E9E9E9",
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingTop: '20px',
          paddingBottom: '20px',
          overflow: 'hidden',
          borderTopLeftRadius: isFirstInSection ? '16px' : '0',
          transition: 'border-radius 0.3s ease',
        }}
        initial={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }}
        animate={
          isExiting
            ? { opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0, marginTop: 0, marginBottom: 0 }
            : { opacity: 1, height: 'auto', paddingTop: '20px', paddingBottom: '20px' }
        }
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {/* Icon */}
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: '40px',
            height: '40px',
            backgroundColor: iconBgColor,
            borderRadius: '50%',
          }}
        >
          <IconComponent className="w-5 h-5" style={{ color: iconColor }} />
        </div>

        {/* Text and Button Container */}
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          {/* Text */}
          <p
            style={{
              color: '#ffffff',
              fontSize: 'var(--text-sm)',
              lineHeight: '1.5',
              fontWeight: 'var(--font-weight-normal)',
            }}
          >
            {displayText}
          </p>

          {/* Close button - bottom right */}
          {tipData.isDismissible && (
            <div className="flex justify-end">
              <motion.button
                onClick={handleDismiss}
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  paddingLeft: '14px',
                  paddingRight: '14px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  borderRadius: 'var(--radius-button)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  minHeight: '36px',
                }}
                whileTap={{ scale: 0.96 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                }}
              >
                {displayCtaLabel}
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Card view (default)
  if (!isVisible) return null;

  return (
    <motion.div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: backgroundColor,
        borderRadius: 'var(--radius-card)',
      }}
      initial={{ opacity: 0, x: -20 }}
      animate={
        isExiting
          ? { opacity: 0, scale: 0.95, height: 0, marginBottom: 0 }
          : { opacity: 1, x: 0 }
      }
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="flex items-start gap-4 p-5">
        {/* Icon */}
        <div className="flex-shrink-0 pt-0.5">
          <div
            className="flex items-center justify-center"
            style={{
              width: '44px',
              height: '44px',
              backgroundColor: iconBgColor,
              borderRadius: 'var(--radius-button)',
            }}
          >
            <IconComponent className="w-5 h-5" style={{ color: iconColor }} />
          </div>
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0 pt-0.5 pr-2 flex flex-col">
          <p
            style={{
              color: '#ffffff',
              fontSize: 'var(--text-sm)',
              lineHeight: '1.5',
              fontWeight: 'var(--font-weight-normal)',
              marginBottom: '12px',
              flex: '1',
            }}
          >
            {displayText}
          </p>
          
          {/* CTA Button */}
          {tipData.isDismissible && (
            <motion.button
              onClick={handleDismiss}
              className="inline-flex items-center justify-center transition-all self-end"
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'rgba(255, 255, 255, 0.9)',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingTop: '6px',
                paddingBottom: '6px',
                borderRadius: 'var(--radius-button)',
                minHeight: '32px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
              whileTap={{ scale: 0.96 }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
              }}
              aria-label={displayCtaLabel}
            >
              {displayCtaLabel}
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
}